import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  apiGetTimekeepingSettings, apiUpdateTimekeepingSettings, type TimekeepingSettingsDTO,
} from "@/lib/api";

export function TimekeepingSettingsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["timekeeping-settings"], queryFn: apiGetTimekeepingSettings });
  const [form, setForm] = useState<TimekeepingSettingsDTO | null>(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const saveMut = useMutation({
    mutationFn: (payload: TimekeepingSettingsDTO) => apiUpdateTimekeepingSettings(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["timekeeping-settings"] });
      toast.success(t("timekeeping.settings.saved", { defaultValue: "Settings saved" }));
    },
    onError: (e: any) => toast.error(e?.message ?? "Error"),
  });

  if (isLoading || !form) return <Skeleton className="h-56 w-full" />;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>{t("timekeeping.settings.title", { defaultValue: "Timekeeping settings" })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label>{t("timekeeping.settings.requiredHours", { defaultValue: "Default required weekly hours" })}</Label>
          <Input type="number" min={0} max={168} step={0.5} className="mt-1 w-40"
            value={form.defaultRequiredHours}
            onChange={(e) => setForm({ ...form, defaultRequiredHours: Number(e.target.value) })} />
        </div>
        <div>
          <Label>{t("timekeeping.settings.maxDaily", { defaultValue: "Maximum hours per day" })}</Label>
          <Input type="number" min={0} max={24} step={0.5} className="mt-1 w-40"
            value={form.maxDailyHours}
            onChange={(e) => setForm({ ...form, maxDailyHours: Number(e.target.value) })} />
        </div>
        <div className="flex items-center justify-between">
          <Label>{t("timekeeping.settings.allowFuture", { defaultValue: "Allow logging future dates" })}</Label>
          <Switch checked={form.allowFutureDates} onCheckedChange={(v) => setForm({ ...form, allowFutureDates: v })} />
        </div>
        <div className="flex items-center justify-between">
          <Label>{t("timekeeping.settings.copyNotes", { defaultValue: "Copy notes by default when copying a week" })}</Label>
          <Switch checked={form.copyNotesDefault} onCheckedChange={(v) => setForm({ ...form, copyNotesDefault: v })} />
        </div>
        <Button onClick={() => saveMut.mutate(form)} disabled={saveMut.isPending}>
          {t("common.save", { defaultValue: "Save" })}
        </Button>
      </CardContent>
    </Card>
  );
}
